/**
 * Widget Settings Component
 * ==========================
 * Admin UI for configuring website widget
 *
 * Added: 2025-11-17 - Website Widget Module Phase 1
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material';
import {
  Save,
  Refresh,
  Code,
  Palette,
  Message,
  Schedule,
  Security,
  Settings as SettingsIcon,
  Add,
  Delete,
  ContentCopy,
} from '@mui/icons-material';
import { SketchPicker, ColorResult } from 'react-color';
import api from '../services/api';
import {
  WidgetConfigAdminResponse,
  WidgetConfigUpdate,
  WidgetPosition,
  WidgetStyle,
  DarkMode,
  AttributeType,
  PreChatFormField,
  BusinessHoursConfig,
  WidgetInstallationCodeResponse,
} from '../types/widget';
import WidgetPreview from './WidgetPreview';

interface WidgetSettingsProps {
  inboxId: number;
  onClose: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const WidgetSettings: React.FC<WidgetSettingsProps> = ({ inboxId, onClose }) => {
  const [config, setConfig] = useState<WidgetConfigAdminResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [installationCode, setInstallationCode] = useState<string>('');
  const [showInstallDialog, setShowInstallDialog] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState<string>('html'); // UPDATED: 2025-12-04 - Platform selection

  useEffect(() => {
    fetchConfig();
  }, [inboxId]);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/inboxes/${inboxId}/widget-config`);
      setConfig(response.data);
    } catch (error: any) {
      if (error.response?.status === 404) {
        // Config doesn't exist, create default
        await createDefaultConfig();
      } else {
        setSnackbar({ open: true, message: 'Failed to load widget configuration', severity: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  const createDefaultConfig = async () => {
    try {
      const defaultConfig = {
        widget_color: '#1f93ff',
        position: WidgetPosition.RIGHT,
        widget_style: WidgetStyle.STANDARD,
        launcher_title: 'Chat with us',
        welcome_heading: 'Hi there!',
        welcome_tagline: 'We are here to help',
        pre_chat_form_enabled: false,
        business_hours_enabled: false,
        email_collect_enabled: true,
        csat_enabled: true,
        auto_greeting_enabled: false,
        hmac_enabled: false,
        dark_mode: DarkMode.AUTO,
        locale: 'en',
        reply_time: 'A few minutes',
      };

      const response = await api.post(`/inboxes/${inboxId}/widget-config`, defaultConfig);
      setConfig(response.data);
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to create widget configuration', severity: 'error' });
    }
  };

  const handleSave = async () => {
    if (!config) return;

    try {
      setSaving(true);

      const updateData: WidgetConfigUpdate = {
        widget_color: config.widget_color,
        position: config.position as WidgetPosition,
        widget_style: config.widget_style as WidgetStyle,
        launcher_title: config.launcher_title,
        welcome_heading: config.welcome_heading,
        welcome_tagline: config.welcome_tagline,
        pre_chat_form_enabled: config.pre_chat_form_enabled,
        pre_chat_form_message: config.pre_chat_form_message,
        pre_chat_form_fields: config.pre_chat_form_fields,
        business_hours_enabled: config.business_hours_enabled,
        business_hours: config.business_hours,
        timezone: config.timezone,
        offline_message: config.offline_message,
        email_collect_enabled: config.email_collect_enabled,
        csat_enabled: config.csat_enabled,
        auto_greeting_enabled: config.auto_greeting_enabled,
        auto_greeting_message: config.auto_greeting_message,
        hmac_enabled: config.hmac_enabled,
        allowed_domains: config.allowed_domains,
        dark_mode: config.dark_mode as DarkMode,
        locale: config.locale,
        reply_time: config.reply_time,
      };

      const response = await api.put(`/inboxes/${inboxId}/widget-config`, updateData);
      setConfig(response.data);
      setSnackbar({ open: true, message: 'Widget configuration saved successfully', severity: 'success' });

      // If installation dialog is open, refresh the installation code to reflect changes
      if (showInstallDialog) {
        try {
          const codeResponse = await api.get<WidgetInstallationCodeResponse>(`/inboxes/${inboxId}/widget-script`);
          setInstallationCode(codeResponse.data.installation_code);
        } catch (error) {
          console.error('Failed to refresh installation code:', error);
          // Don't show error to user as save was successful
        }
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to save widget configuration', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleGetInstallationCode = async (platform: string = 'html') => {
    // UPDATED: 2025-12-04 - Fetch platform-specific installation code
    try {
      const response = await api.get<WidgetInstallationCodeResponse>(
        `/inboxes/${inboxId}/widget-script?platform=${platform}`
      );
      setInstallationCode(response.data.installation_code);
      setShowInstallDialog(true);
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to get installation code', severity: 'error' });
    }
  };

  const handlePlatformChange = async (_event: React.SyntheticEvent, newPlatform: string) => {
    // UPDATED: 2025-12-04 - Handle platform tab changes
    setSelectedPlatform(newPlatform);
    try {
      const response = await api.get<WidgetInstallationCodeResponse>(
        `/inboxes/${inboxId}/widget-script?platform=${newPlatform}`
      );
      setInstallationCode(response.data.installation_code);
    } catch (error) {
      setSnackbar({ open: true, message: `Failed to get ${newPlatform} installation code`, severity: 'error' });
    }
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(installationCode);
    setSnackbar({ open: true, message: 'Installation code copied to clipboard', severity: 'success' });
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>Loading widget configuration...</Typography>
      </Box>
    );
  }

  if (!config) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Failed to load widget configuration</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">Widget Settings</Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<Code />}
            onClick={() => handleGetInstallationCode()}
            sx={{ mr: 2 }}
          >
            Get Installation Code
          </Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchConfig}
            sx={{ mr: 2 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Box>

      {/* Main Content Grid */}
      <Grid container spacing={0}>
        {/* Left Side - Settings Tabs */}
        <Grid item xs={12} lg={7}>
          {/* Tabs */}
          <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tab icon={<Palette />} label="Appearance" />
            <Tab icon={<Message />} label="Messages" />
            <Tab icon={<Schedule />} label="Business Hours" />
            <Tab icon={<Security />} label="Security" />
            <Tab icon={<SettingsIcon />} label="Advanced" />
          </Tabs>

          {/* Appearance Tab */}
          <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Widget Appearance</Typography>

                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" gutterBottom>Widget Color</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 50,
                        height: 50,
                        backgroundColor: config.widget_color,
                        border: '1px solid #ccc',
                        borderRadius: 1,
                        cursor: 'pointer',
                      }}
                      onClick={() => setShowColorPicker(!showColorPicker)}
                    />
                    <TextField
                      value={config.widget_color}
                      onChange={(e) => setConfig({ ...config, widget_color: e.target.value })}
                      size="small"
                      sx={{ width: 120 }}
                    />
                  </Box>
                  {showColorPicker && (
                    <Box sx={{ position: 'absolute', zIndex: 2 }}>
                      <Box
                        sx={{ position: 'fixed', top: 0, right: 0, bottom: 0, left: 0 }}
                        onClick={() => setShowColorPicker(false)}
                      />
                      <SketchPicker
                        color={config.widget_color}
                        onChange={(color: ColorResult) => setConfig({ ...config, widget_color: color.hex })}
                      />
                    </Box>
                  )}
                </Box>

                <FormControl fullWidth sx={{ mt: 3 }}>
                  <InputLabel>Position</InputLabel>
                  <Select
                    value={config.position}
                    label="Position"
                    onChange={(e) => setConfig({ ...config, position: e.target.value as WidgetPosition})}
                  >
                    <MenuItem value={WidgetPosition.LEFT}>Left</MenuItem>
                    <MenuItem value={WidgetPosition.RIGHT}>Right</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth sx={{ mt: 3 }}>
                  <InputLabel>Widget Style</InputLabel>
                  <Select
                    value={config.widget_style}
                    label="Widget Style"
                    onChange={(e) => setConfig({ ...config, widget_style: e.target.value as WidgetStyle })}
                  >
                    <MenuItem value={WidgetStyle.STANDARD}>Standard</MenuItem>
                    <MenuItem value={WidgetStyle.EXPANDED}>Expanded</MenuItem>
                    <MenuItem value={WidgetStyle.COMPACT}>Compact</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="Launcher Title"
                  value={config.launcher_title}
                  onChange={(e) => setConfig({ ...config, launcher_title: e.target.value })}
                  sx={{ mt: 3 }}
                />

                <FormControl fullWidth sx={{ mt: 3 }}>
                  <InputLabel>Dark Mode</InputLabel>
                  <Select
                    value={config.dark_mode}
                    label="Dark Mode"
                    onChange={(e) => setConfig({ ...config, dark_mode: e.target.value as DarkMode })}
                  >
                    <MenuItem value={DarkMode.LIGHT}>Light</MenuItem>
                    <MenuItem value={DarkMode.DARK}>Dark</MenuItem>
                    <MenuItem value={DarkMode.AUTO}>Auto</MenuItem>
                  </Select>
                </FormControl>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Widget Preview</Typography>
                <Box
                  sx={{
                    height: 400,
                    border: '1px solid #ccc',
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f5f5f5',
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Widget preview will appear here
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Messages Tab */}
      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Welcome Messages</Typography>

                <TextField
                  fullWidth
                  label="Welcome Heading"
                  value={config.welcome_heading}
                  onChange={(e) => setConfig({ ...config, welcome_heading: e.target.value })}
                  sx={{ mt: 2 }}
                />

                <TextField
                  fullWidth
                  label="Welcome Tagline"
                  value={config.welcome_tagline}
                  onChange={(e) => setConfig({ ...config, welcome_tagline: e.target.value })}
                  sx={{ mt: 3 }}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={config.auto_greeting_enabled}
                      onChange={(e) => setConfig({ ...config, auto_greeting_enabled: e.target.checked })}
                    />
                  }
                  label="Enable Auto Greeting"
                  sx={{ mt: 3 }}
                />

                {config.auto_greeting_enabled && (
                  <TextField
                    fullWidth
                    label="Auto Greeting Message"
                    multiline
                    rows={3}
                    value={config.auto_greeting_message || ''}
                    onChange={(e) => setConfig({ ...config, auto_greeting_message: e.target.value })}
                    sx={{ mt: 2 }}
                  />
                )}

                <Divider sx={{ my: 3 }} />

                <Typography variant="h6" gutterBottom>Features</Typography>

                <FormControlLabel
                  control={
                    <Switch
                      checked={config.email_collect_enabled}
                      onChange={(e) => setConfig({ ...config, email_collect_enabled: e.target.checked })}
                    />
                  }
                  label="Collect Email Addresses"
                  sx={{ display: 'block', mt: 2 }}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={config.csat_enabled}
                      onChange={(e) => setConfig({ ...config, csat_enabled: e.target.checked })}
                    />
                  }
                  label="Enable CSAT Surveys"
                  sx={{ display: 'block', mt: 2 }}
                />

                <TextField
                  fullWidth
                  label="Expected Reply Time"
                  value={config.reply_time}
                  onChange={(e) => setConfig({ ...config, reply_time: e.target.value })}
                  helperText="e.g., 'A few minutes', 'Within an hour'"
                  sx={{ mt: 3 }}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Business Hours Tab */}
      <TabPanel value={tabValue} index={2}>
        <Card>
          <CardContent>
            <FormControlLabel
              control={
                <Switch
                  checked={config.business_hours_enabled}
                  onChange={(e) => setConfig({ ...config, business_hours_enabled: e.target.checked })}
                />
              }
              label="Enable Business Hours"
            />

            {config.business_hours_enabled && (
              <Box sx={{ mt: 3 }}>
                <TextField
                  fullWidth
                  label="Timezone"
                  value={config.timezone}
                  onChange={(e) => setConfig({ ...config, timezone: e.target.value })}
                  helperText="e.g., America/New_York, Europe/London, Asia/Tokyo"
                  sx={{ mb: 3 }}
                />

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Business hours configuration will be available in Phase 2
                </Typography>

                <TextField
                  fullWidth
                  label="Offline Message"
                  multiline
                  rows={3}
                  value={config.offline_message || ''}
                  onChange={(e) => setConfig({ ...config, offline_message: e.target.value })}
                  helperText="Message shown when outside business hours"
                />
              </Box>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Security Tab */}
      <TabPanel value={tabValue} index={3}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Security Settings</Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={config.hmac_enabled}
                  onChange={(e) => setConfig({ ...config, hmac_enabled: e.target.checked })}
                />
              }
              label="Enable HMAC Verification"
              sx={{ display: 'block', mt: 2 }}
            />

            {config.hmac_enabled && config.hmac_secret && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2" gutterBottom>
                  <strong>HMAC Secret:</strong>
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {config.hmac_secret}
                </Typography>
              </Alert>
            )}

            <Divider sx={{ my: 3 }} />

            <Typography variant="body2" gutterBottom>Allowed Domains (CORS)</Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={config.allowed_domains?.join('\n') || ''}
              onChange={(e) => setConfig({ ...config, allowed_domains: e.target.value.split('\n').filter(d => d.trim()) })}
              placeholder="https://example.com&#10;https://app.example.com"
              helperText="One domain per line"
              sx={{ mt: 1 }}
            />
          </CardContent>
        </Card>
      </TabPanel>

      {/* Advanced Tab */}
      <TabPanel value={tabValue} index={4}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Advanced Settings</Typography>

            <TextField
              fullWidth
              label="Locale"
              value={config.locale}
              onChange={(e) => setConfig({ ...config, locale: e.target.value })}
              helperText="Language code (e.g., en, es, fr)"
              sx={{ mt: 2 }}
            />

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>Widget Information</Typography>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell>Widget ID</TableCell>
                  <TableCell>{config.id}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Channel ID</TableCell>
                  <TableCell>{config.channel_id}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Account ID</TableCell>
                  <TableCell>{config.account_id}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Created</TableCell>
                  <TableCell>{new Date(config.created_at).toLocaleString()}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Last Updated</TableCell>
                  <TableCell>{new Date(config.updated_at).toLocaleString()}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </TabPanel>
        </Grid>

        {/* Right Side - Widget Preview */}
        <Grid item xs={12} lg={5} sx={{ borderLeft: { lg: 1 }, borderColor: { lg: 'divider' } }}>
          <Box sx={{ position: 'sticky', top: 20, p: 2 }}>
            <WidgetPreview
              key={`${config.widget_color}-${config.position}-${config.launcher_title}-${config.dark_mode}-${config.widget_style}`}
              config={{
                widget_color: config.widget_color,
                position: config.position as WidgetPosition,
                launcher_title: config.launcher_title,
                welcome_heading: config.welcome_heading,
                welcome_tagline: config.welcome_tagline,
                reply_time: config.reply_time,
                widget_style: config.widget_style as 'standard' | 'expanded' | 'compact',
                dark_mode: config.dark_mode as 'light' | 'dark' | 'auto',
              }}
            />
          </Box>
        </Grid>
      </Grid>

      {/* Installation Code Dialog - UPDATED: 2025-12-04 - Platform-specific installation code */}
      <Dialog open={showInstallDialog} onClose={() => setShowInstallDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Widget Installation Code</Typography>
            <Chip
              label="Platform Independent"
              color="primary"
              size="small"
              sx={{ fontWeight: 600 }}
            />
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={600} mb={1}>
              Choose your platform below and copy the installation code
            </Typography>
            <Typography variant="caption">
              The Converse.ai widget integrates seamlessly with any frontend framework or platform.
              All conversations will be received in your Converse inbox.
            </Typography>
          </Alert>

          {/* Platform Selection Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs
              value={selectedPlatform}
              onChange={handlePlatformChange}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label="HTML/JS" value="html" />
              <Tab label="React" value="react" />
              <Tab label="Next.js" value="nextjs" />
              <Tab label="TypeScript" value="typescript" />
              <Tab label="PHP/Laravel" value="php" />
              <Tab label="Vue.js" value="vue" />
            </Tabs>
          </Box>

          {/* Installation Instructions */}
          {selectedPlatform === 'html' && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight={600}>
                HTML/JavaScript Installation:
              </Typography>
              <Typography variant="caption">
                Copy and paste this code into your website's HTML, just before the closing &lt;/body&gt; tag.
              </Typography>
            </Alert>
          )}

          {selectedPlatform === 'react' && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight={600}>
                React Installation:
              </Typography>
              <Typography variant="caption" component="div">
                1. Copy the code below into your React component<br />
                2. Import and use the ConverseWidget component in your App<br />
                3. The widget will automatically load and initialize
              </Typography>
            </Alert>
          )}

          {selectedPlatform === 'nextjs' && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight={600}>
                Next.js Installation:
              </Typography>
              <Typography variant="caption" component="div">
                1. Create components/ConverseWidget.tsx with the code below<br />
                2. Add to your layout.tsx (App Router) or _app.tsx (Pages Router)<br />
                3. Supports both Next.js 13+ App Router and Pages Router
              </Typography>
            </Alert>
          )}

          {selectedPlatform === 'typescript' && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight={600}>
                TypeScript Installation:
              </Typography>
              <Typography variant="caption" component="div">
                1. Add type declarations (converse-widget.d.ts)<br />
                2. Create widget manager class with full type safety<br />
                3. Initialize the widget with proper error handling
              </Typography>
            </Alert>
          )}

          {selectedPlatform === 'php' && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight={600}>
                PHP/Laravel Installation:
              </Typography>
              <Typography variant="caption" component="div">
                1. Add to your Blade layout template (resources/views/layouts/app.blade.php)<br />
                2. Automatically includes authenticated user data if available<br />
                3. Works with Laravel authentication out of the box
              </Typography>
            </Alert>
          )}

          {selectedPlatform === 'vue' && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight={600}>
                Vue.js Installation:
              </Typography>
              <Typography variant="caption" component="div">
                1. Create components/ConverseWidget.vue with the code below<br />
                2. Import and add to your App.vue or main component<br />
                3. Supports both Vue 2 and Vue 3 (Composition API)
              </Typography>
            </Alert>
          )}

          {/* Code Display */}
          <Paper sx={{ p: 2, backgroundColor: '#f5f5f5', position: 'relative', maxHeight: '500px', overflow: 'auto' }}>
            <IconButton
              onClick={handleCopyCode}
              sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, backgroundColor: 'white' }}
              size="small"
            >
              <ContentCopy />
            </IconButton>
            <Typography
              component="pre"
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.8rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
                mt: 2,
                pr: 5,
              }}
            >
              {installationCode}
            </Typography>
          </Paper>

          {/* Widget SDK Documentation */}
          <Box sx={{ mt: 3, p: 2, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
            <Typography variant="body2" fontWeight={600} mb={1}>
              📚 Widget SDK Documentation
            </Typography>
            <Typography variant="caption" component="div" mb={1}>
              For advanced usage, custom styling, and API reference, check out the widget SDK documentation.
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => window.open('/widget-sdk/README.md', '_blank')}
            >
              View Documentation
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowInstallDialog(false)}>Close</Button>
          <Button onClick={handleCopyCode} variant="contained" startIcon={<ContentCopy />}>
            Copy {selectedPlatform.toUpperCase()} Code
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WidgetSettings;
